/**
 * @description Domain URL construction utilities for the admin portal
 *
 * Key features:
 * - Environment-aware SO domain URLs (production vs staging)
 * - Channel Partner host preview is current-env only (vanity-off)
 * - Single source of truth for lynx.build domain construction
 *
 * @notes
 * - SO staging uses {slug}.staging.lynx.build (prod-parity OAuth; LYN-27)
 * - CP staging uses app.{slug}.staging.lynx.build — never both envs on one release
 * - CP Admin Host preview mirrors the backend invite-mint helper (LYN-337):
 *   admin.{cp_domain} when vanity is connected, else admin.{slug}.staging.lynx.build /
 *   admin.{slug}.lynx.build. The API adminHost field wins when present.
 * - Set REACT_APP_LYNX_BUILD_SUFFIX="-staging" in CF Pages preview env vars
 * - Production leaves it unset (defaults to empty string)
 * - SA Slug occupancy door is the API occupancyHost only. The admin portal
 *   does not recompute nest or factory. A missing host stays blank.
 * - Do not invent per-merchant FQDNs here
 */

const LYNX_BUILD_SUFFIX = process.env.REACT_APP_LYNX_BUILD_SUFFIX || '';

/**
 * Build the full SO subdomain URL for display
 * @param {string} slug - SO slug (e.g., 'acme123')
 * @returns {string} Full domain (e.g., 'acme123.lynx.build' or 'acme123.staging.lynx.build')
 */
export function getSODomainUrl(slug) {
  if (LYNX_BUILD_SUFFIX) {
    return `${slug}.staging.lynx.build`;
  }
  return `${slug}.lynx.build`;
}

/**
 * Occupancy door for SA Slug / create preview.
 * The API occupancyHost is the door. This module does not recompute nest
 * or factory from the partner row.
 *
 * @param {{ occupancyHost?: string|null }|string|null} domain
 * @returns {string}
 */
export function getSoOccupancyDoorUrl(domain) {
  if (!domain || typeof domain === 'string') {
    return '';
  }
  return String(domain.occupancyHost || '').trim().toLowerCase();
}

/**
 * Current-env CP app host (vanity-off). Never both envs on one release.
 * @param {string} slug
 * @returns {string} `{slug}.lynx.build` in prod, `app.{slug}.staging.lynx.build` on staging
 */
export function getChannelPartnerHostPreview(slug) {
  const normalized = slug || 'slug';
  if (LYNX_BUILD_SUFFIX) {
    return `app.${normalized}.staging.lynx.build`;
  }
  return `${normalized}.lynx.build`;
}

/**
 * Current-env CP admin host. Prefers the API adminHost (backend invite-mint helper).
 * Fallback mirrors it: connected vanity → admin.{cp_domain}; else admin factory.
 * Never the app host.
 * @param {{ slug?: string, adminHost?: string|null, customDomainStatus?: string|null, customDomainBase?: string|null }} partner
 * @returns {string} admin host for the current env
 */
export function getChannelPartnerAdminHostPreview(partner) {
  const fromApi = String(partner?.adminHost || '').trim().toLowerCase();
  if (fromApi) {
    return fromApi;
  }
  const vanity = partner?.customDomainStatus === 'verified'
    ? String(partner?.customDomainBase || '').trim().toLowerCase()
    : '';
  if (vanity) {
    return `admin.${vanity}`;
  }
  const normalized = String(partner?.slug || '').toLowerCase().trim() || 'slug';
  if (LYNX_BUILD_SUFFIX) {
    return `admin.${normalized}.staging.lynx.build`;
  }
  return `admin.${normalized}.lynx.build`;
}
